import {t} from "i18next";
import {select, take} from "redux-saga/effects";
import {FieldNames} from "../../../models/fields";
import {
  fetchAllUsersFailure,
  fetchAllUsersSuccess
} from "../../features/usersSlice";
import {selectAllUsers} from "../../selectors";

/**
 *
 * @param {Array<Object>} users
 * @param {Object} incident
 * @returns {Array<Object>} users
 */
export const filterDeletedUsers = (users, incident) => {
  const {
    incidents,
    indicators,
    relationships,
    sightings,
    investigations,
    loadedResults,
    casebooks
  } = incident;

  const uNames = userNames(users);
  const ids = userIds(users);

  const handleDeletedUsers = (arr, fieldName, values = uNames) =>
    arr.forEach(
      (obj) => (obj[fieldName] = hideDeletedUsers(obj[fieldName], values))
    );

  // Filter incident assignees
  if (incident.assignees) {
    incident.assignees = incident.assignees.map((ia) =>
      hideDeletedUsers(ia, ids)
    );
  }

  // Filter incident events
  if (incident.events) {
    incident.events = incident.events.filter((ie) => {
      if (ie.entity) {
        ie.entity = filterDeletedUsers(users, ie.entity);
      }
      const fNames = ie.fields && ie.fields.map((fld) => fld.field);
      if (!fNames) return true;
      const i = fNames.indexOf("assignees");
      if (i > -1) {
        const field = ie.fields[i];
        if (field.change.after) {
          field.change.after = field.change.after.filter((id) =>
            ids.includes(id)
          );
        }
        if (field.change.before) {
          field.change.before = field.change.before.filter((id) =>
            ids.includes(id)
          );
        }
      }
      return true;
    });
  }

  // Filter incident.incidents assignees & owner
  if (incidents) {
    incidents.forEach((inc) => {
      inc.owner = hideDeletedUsers(inc.owner, ids);
      inc.assignees = inc.assignees
        ? inc.assignees.map((assignee) => hideDeletedUsers(assignee, ids))
        : [];
    });
  }

  // Filter incident indicators
  if (indicators) {
    handleDeletedUsers(indicators, FieldNames.owner);
  }

  // Filter incident relationships owner
  if (relationships) {
    handleDeletedUsers(relationships, FieldNames.owner);
  }

  // Filter incident sightings owner
  if (sightings) {
    handleDeletedUsers(sightings, FieldNames.owner);
  }

  // Filter incident investigations owner
  if (investigations) {
    handleDeletedUsers(investigations, FieldNames.owner, ids);
    investigations.forEach(
      (ind) => (ind.source = hideDeletedUsers(ind.source, uNames))
    );
  }

  // Filter incident loadedResults owner
  if (loadedResults) {
    handleDeletedUsers(loadedResults, FieldNames.owner, ids);
    handleDeletedUsers(loadedResults, FieldNames.source);
  }

  // Filter incident casebooks owner
  if (casebooks) {
    handleDeletedUsers(casebooks, FieldNames.owner, ids);
    handleDeletedUsers(casebooks, FieldNames.source);
  }

  return incident;
};

/**
 *
 * @param {Array<Object>} users
 * @returns {Array<String>} Array of user id's.
 */
export const userNames = (users) =>
  users.map((user) => user[FieldNames.userName]);

/**
 *
 * @param {Array<Object>} users
 * @returns {Array<String>} Array of user id's.
 */
export const userIds = (users) => users.map((user) => user[FieldNames.userId]);

/**
 *
 * @param {String} fieldName
 * @param {Array<String>} values
 * @param {*} condition
 * @returns
 */
export const hideDeletedUsers = (fieldName, values, condition = false) =>
  values.includes(fieldName) || condition ? fieldName : t("users.deleted");

/**
 * Helper function to get user objects from state.
 * - On app startup the fetchAllUsersSuccess Action may not have completed yet
 *   so we wait for it to complete.
 * - On future searches, we can safely assume the store was populared on
 *   startup, so select and retun the state object.
 * @returns {Array<Object>} Array of user objects from Store.
 */
export function* takeUserList() {
  let users = yield select(selectAllUsers);
  if (!users || users.length <= 0) {
    yield take([fetchAllUsersSuccess, fetchAllUsersFailure]);
    users = yield select(selectAllUsers);
  }
  return users;
}
